import type { Edge, Node } from '@xyflow/react'
import type { MindMapEdge } from '@bandos/shared'
import type { ReactFlowNodeData } from '../types/mindmap-components'

/**
 * Calculate the total number of descendants (children + grandchildren + etc.) for a node
 * @param nodeId - The ID of the node to calculate descendants for
 * @param edges - Array of edges to traverse
 * @param nodes - Array of all nodes (for validation)
 * @param visited - Set of visited node IDs to prevent infinite recursion
 * @returns Total count of descendants
 */
export function calculateNodeChildrenCount(
  nodeId: string,
  edges: Edge[] | MindMapEdge[],
  nodes?: Node<ReactFlowNodeData>[] | Array<{ id: string }>,
  visited: Set<string> = new Set()
): number {
  // Prevent infinite recursion
  if (visited.has(nodeId)) return 0
  visited.add(nodeId)

  // Count direct children
  const directChildren = edges.filter((edge) => edge.source === nodeId).length

  // Count indirect children (recursively)
  const indirectChildren = edges
    .filter((edge) => edge.source === nodeId)
    .reduce((count, edge) => {
      // Validate target node exists if nodes array provided
      if (nodes) {
        const targetNode = nodes.find((n) => n.id === edge.target)
        if (!targetNode) return count
      }
      return count + calculateNodeChildrenCount(edge.target, edges, nodes, new Set(visited))
    }, 0)

  return directChildren + indirectChildren
}
